import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';

const ShortBreaksTab = ({ showToast }) => {
    const [shortBreaks, setShortBreaks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        duration: '',
        destination: '',
        image_url: '',
        highlights: ['', '', '', ''],
        description: '',
        active: true,
        display_order: 0
    });

    useEffect(() => {
        fetchShortBreaks();
    }, []);

    const fetchShortBreaks = async () => {
        setLoading(true);
        const { data, error } = await supabase
            .from('short_breaks')
            .select('*')
            .order('display_order');
        if (!error) setShortBreaks(data || []);
        setLoading(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const cleanHighlights = formData.highlights.filter(h => h.trim() !== '');
        const dataToSave = { ...formData, highlights: cleanHighlights };

        if (editingId) {
            const { error } = await supabase
                .from('short_breaks')
                .update(dataToSave)
                .eq('id', editingId);
            if (!error) {
                showToast('Short break updated successfully', 'success');
                closeModal();
            } else {
                showToast('Error updating short break', 'error');
            }
        } else {
            const { error } = await supabase
                .from('short_breaks')
                .insert([dataToSave]);
            if (!error) {
                showToast('Short break added successfully', 'success');
                closeModal();
            } else {
                showToast('Error adding short break', 'error');
            }
        }

        fetchShortBreaks();
    };


    const openModal = (shortBreak = null) => {
        if (shortBreak) {
            setEditingId(shortBreak.id);
            setFormData({
                title: shortBreak.title,
                duration: shortBreak.duration,
                destination: shortBreak.destination,
                image_url: shortBreak.image_url || '',
                highlights: [...(shortBreak.highlights || []), '', '', '', ''].slice(0, 4),
                description: shortBreak.description || '',
                active: shortBreak.active,
                display_order: shortBreak.display_order
            });
        } else {
            setEditingId(null);
            setFormData({
                title: '',
                duration: '',
                destination: '',
                image_url: '',
                highlights: ['', '', '', ''],
                description: '',
                active: true,
                display_order: shortBreaks.length
            });
        }
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setEditingId(null);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this short break?')) return;
        const { error } = await supabase.from('short_breaks').delete().eq('id', id);
        if (!error) {
            showToast('Short break deleted successfully', 'success');
            fetchShortBreaks();
        } else {
            showToast('Error deleting short break', 'error');
        }
    };

    const handleHighlightChange = (index, value) => {
        const newHighlights = [...formData.highlights];
        newHighlights[index] = value;
        setFormData({ ...formData, highlights: newHighlights });
    };

    return (
        <div className="admin-tab-content">
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 30 }}>
                <div>
                    <h2 style={{ fontSize: 24, fontWeight: 800, color: 'var(--text-1)', marginBottom: 8 }}>
                        <i className="fa-solid fa-calendar-days" style={{ marginRight: 10, color: 'var(--gold)' }}></i>
                        Short Breaks Manager
                    </h2>
                    <p style={{ color: 'var(--text-3)', fontSize: 14 }}>
                        {shortBreaks.length} packages • Manage short break packages displayed on the website
                    </p>
                </div>
                <button 
                    onClick={() => openModal()}
                    style={{ 
                        padding: '12px 24px',
                        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: 10,
                        fontSize: 15,
                        fontWeight: 700,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        boxShadow: '0 4px 12px rgba(245, 158, 11, 0.25)',
                        transition: 'all 0.2s ease',
                        height: 'fit-content'
                    }}
                    onMouseOver={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 8px 20px rgba(245, 158, 11, 0.35)';
                    }}
                    onMouseOut={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(245, 158, 11, 0.25)';
                    }}
                >
                    <i className="fa-solid fa-plus" style={{ fontSize: 14 }}></i>
                    Add Short Break
                </button>
            </div>

            {/* Cards Grid */}
            {loading ? (
                <div className="admin-loading"><div className="admin-spinner" /><p>Loading...</p></div>
            ) : shortBreaks.length === 0 ? (
                <div style={{
                    textAlign: 'center',
                    padding: '80px 20px',
                    background: 'white',
                    borderRadius: 16,
                    border: '2px dashed #e2e8f0'
                }}>
                    <div style={{
                        width: 80,
                        height: 80,
                        background: '#f8fafc',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 20px',
                        fontSize: 36,
                        color: '#cbd5e1'
                    }}>
                        <i className="fa-solid fa-calendar-xmark"></i>
                    </div>
                    <h3 style={{ fontSize: 20, fontWeight: 700, color: '#1e293b', marginBottom: 8 }}>
                        No short breaks yet
                    </h3>
                    <p style={{ fontSize: 14, color: '#64748b', marginBottom: 24 }}>
                        Start by adding your first short break package
                    </p>
                    <button 
                        onClick={() => openModal()}
                        style={{
                            padding: '12px 28px',
                            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                            color: 'white',
                            border: 'none',
                            borderRadius: 10,
                            fontSize: 15,
                            fontWeight: 600,
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 8,
                            transition: 'all 0.2s ease'
                        }}
                        onMouseOver={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 8px 20px rgba(245, 158, 11, 0.3)';
                        }}
                        onMouseOut={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = 'none';
                        }}
                    >
                        <i className="fa-solid fa-plus"></i>
                        Add Your First Short Break
                    </button>
                </div>
            ) : (
                <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', 
                    gap: 24 
                }}>
                    {shortBreaks.map((sb) => (
                        <div key={sb.id} style={{ 
                            background: 'white',
                            borderRadius: 16,
                            overflow: 'hidden',
                            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                            transition: 'all 0.3s ease',
                            opacity: sb.active ? 1 : 0.6,
                            position: 'relative',
                            border: '1px solid #e2e8f0'
                        }}>
                            {/* Image */}
                            <div style={{
                                height: 200,
                                backgroundImage: `url(${sb.image_url})`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                position: 'relative'
                            }}>
                                <div style={{
                                    position: 'absolute',
                                    top: 14,
                                    right: 14,
                                    background: 'rgba(30, 41, 59, 0.85)',
                                    backdropFilter: 'blur(10px)',
                                    padding: '7px 14px',
                                    borderRadius: 25,
                                    fontSize: 13,
                                    fontWeight: 700,
                                    color: 'white',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6
                                }}>
                                    <i className="fa-solid fa-clock" style={{ fontSize: 12 }}></i>
                                    {sb.duration}
                                </div>
                                {!sb.active && (
                                    <div style={{
                                        position: 'absolute',
                                        top: 14,
                                        left: 14,
                                        background: '#ef4444',
                                        padding: '6px 14px',
                                        borderRadius: 25,
                                        fontSize: 12,
                                        fontWeight: 700,
                                        color: 'white'
                                    }}>
                                        Inactive
                                    </div>
                                )}
                            </div>

                            {/* Content */}
                            <div style={{ padding: 24 }}>
                                <h3 style={{ 
                                    fontSize: 20, 
                                    fontWeight: 700, 
                                    color: '#1e293b', 
                                    marginBottom: 10,
                                    lineHeight: 1.3
                                }}>
                                    {sb.title}
                                </h3>
                                
                                <div style={{ 
                                    display: 'inline-flex', 
                                    alignItems: 'center', 
                                    gap: 6,
                                    marginBottom: 14,
                                    fontSize: 14,
                                    fontWeight: 600,
                                    color: '#64748b',
                                    background: '#f8fafc',
                                    padding: '6px 12px',
                                    borderRadius: 8
                                }}>
                                    <i className="fa-solid fa-location-dot" style={{ color: '#f59e0b', fontSize: 13 }}></i>
                                    {sb.destination}
                                </div>

                                {sb.description && (
                                    <p style={{ 
                                        fontSize: 14, 
                                        color: '#64748b', 
                                        marginBottom: 16,
                                        lineHeight: 1.6
                                    }}>
                                        {sb.description}
                                    </p>
                                )}

                                {sb.highlights && sb.highlights.length > 0 && (
                                    <div style={{ marginBottom: 20 }}>
                                        {sb.highlights.slice(0, 3).map((highlight, i) => (
                                            <div key={i} style={{
                                                fontSize: 13,
                                                color: '#475569',
                                                marginBottom: 8,
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                gap: 10
                                            }}>
                                                <i className="fa-solid fa-circle-check" style={{ 
                                                    color: '#f59e0b', 
                                                    fontSize: 14,
                                                    marginTop: 2
                                                }}></i>
                                                <span>{highlight}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Actions */}
                                <div style={{ 
                                    display: 'flex', 
                                    gap: 10,
                                    paddingTop: 18,
                                    borderTop: '1px solid #e2e8f0'
                                }}>
                                    <button
                                        onClick={() => openModal(sb)}
                                        style={{ 
                                            flex: 1, 
                                            fontSize: 14,
                                            fontWeight: 600,
                                            padding: '10px 16px',
                                            background: 'white',
                                            color: '#1e293b',
                                            border: '1.5px solid #e2e8f0',
                                            borderRadius: 10,
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: 8,
                                            transition: 'all 0.2s ease'
                                        }}
                                        onMouseOver={(e) => {
                                            e.currentTarget.style.background = '#f8fafc';
                                            e.currentTarget.style.borderColor = '#cbd5e1';
                                        }}
                                        onMouseOut={(e) => {
                                            e.currentTarget.style.background = 'white';
                                            e.currentTarget.style.borderColor = '#e2e8f0';
                                        }}
                                    >
                                        <i className="fa-solid fa-pen" style={{ fontSize: 13 }}></i>
                                        Edit
                                    </button>
                                    <button
                                        onClick={() => handleDelete(sb.id)}
                                        title="Delete"
                                        style={{ 
                                            width: 44,
                                            height: 44,
                                            background: 'white',
                                            color: '#ef4444',
                                            border: '1.5px solid #fee2e2',
                                            borderRadius: 10,
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: 15,
                                            transition: 'all 0.2s ease'
                                        }}
                                        onMouseOver={(e) => {
                                            e.currentTarget.style.background = '#fef2f2';
                                            e.currentTarget.style.borderColor = '#fecaca';
                                        }}
                                        onMouseOut={(e) => {
                                            e.currentTarget.style.background = 'white';
                                            e.currentTarget.style.borderColor = '#fee2e2';
                                        }}
                                    >
                                        <i className="fa-solid fa-trash"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Modal */}
            {isModalOpen && (
                <div 
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0, 0, 0, 0.6)',
                        backdropFilter: 'blur(4px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10000,
                        padding: 20,
                        animation: 'fadeIn 0.2s ease'
                    }}
                    onClick={closeModal}
                >
                    <div 
                        style={{
                            background: 'white',
                            borderRadius: 20,
                            maxWidth: 750,
                            width: '100%',
                            maxHeight: '90vh',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                            animation: 'slideUp 0.3s ease'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Modal Header */}
                        <div style={{
                            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                            padding: '28px 32px',
                            position: 'relative',
                            borderBottom: '1px solid rgba(255, 255, 255, 0.2)'
                        }}>
                            <button 
                                onClick={closeModal}
                                style={{
                                    position: 'absolute',
                                    top: 20,
                                    right: 20,
                                    background: 'rgba(255, 255, 255, 0.2)',
                                    border: 'none',
                                    width: 36,
                                    height: 36,
                                    borderRadius: '50%',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 16,
                                    color: 'white',
                                    transition: 'all 0.2s ease'
                                }}
                                onMouseOver={(e) => {
                                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
                                    e.currentTarget.style.transform = 'rotate(90deg)';
                                }}
                                onMouseOut={(e) => {
                                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                                    e.currentTarget.style.transform = 'rotate(0deg)';
                                }}
                            >
                                <i className="fa-solid fa-times"></i>
                            </button>

                            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                                <div style={{
                                    width: 50,
                                    height: 50,
                                    background: 'rgba(255, 255, 255, 0.2)',
                                    borderRadius: 12,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 22,
                                    color: 'white'
                                }}>
                                    <i className="fa-solid fa-calendar-days"></i>
                                </div>
                                <div>
                                    <h3 style={{ 
                                        fontSize: 24, 
                                        fontWeight: 800, 
                                        color: 'white',
                                        margin: 0,
                                        marginBottom: 4
                                    }}>
                                        {editingId ? 'Edit Short Break' : 'Add New Short Break'}
                                    </h3>
                                    <p style={{ 
                                        fontSize: 14, 
                                        color: 'rgba(255, 255, 255, 0.9)',
                                        margin: 0
                                    }}>
                                        {editingId ? 'Update package details below' : 'Fill in the details to create a new package'}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Modal Body */}
                        <form 
                            onSubmit={handleSubmit}
                            style={{
                                padding: '32px',
                                overflowY: 'auto',
                                flex: 1
                            }}
                        >
                            {/* Title & Duration */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
                                <div>
                                    <label style={{ 
                                        display: 'block',
                                        fontSize: 14,
                                        fontWeight: 600,
                                        color: '#1e293b',
                                        marginBottom: 8
                                    }}>
                                        Package Title <span style={{ color: '#ef4444' }}>*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.title}
                                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                        required
                                        placeholder="e.g., Weekend Goa Getaway"
                                        style={{
                                            width: '100%',
                                            padding: '12px 16px',
                                            border: '2px solid #e2e8f0',
                                            borderRadius: 10,
                                            fontSize: 15,
                                            fontFamily: 'inherit',
                                            transition: 'all 0.2s ease',
                                            outline: 'none'
                                        }}
                                        onFocus={(e) => {
                                            e.currentTarget.style.borderColor = '#f59e0b';
                                            e.currentTarget.style.boxShadow = '0 0 0 3px rgba(245, 158, 11, 0.1)';
                                        }}
                                        onBlur={(e) => {
                                            e.currentTarget.style.borderColor = '#e2e8f0';
                                            e.currentTarget.style.boxShadow = 'none';
                                        }}
                                    />
                                </div>
                                <div>
                                    <label style={{ 
                                        display: 'block',
                                        fontSize: 14,
                                        fontWeight: 600,
                                        color: '#1e293b',
                                        marginBottom: 8
                                    }}>
                                        Duration <span style={{ color: '#ef4444' }}>*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.duration}
                                        onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                                        required
                                        placeholder="e.g., 2 Nights / 3 Days"
                                        style={{
                                            width: '100%',
                                            padding: '12px 16px',
                                            border: '2px solid #e2e8f0',
                                            borderRadius: 10,
                                            fontSize: 15,
                                            fontFamily: 'inherit',
                                            transition: 'all 0.2s ease',
                                            outline: 'none'
                                        }}
                                        onFocus={(e) => {
                                            e.currentTarget.style.borderColor = '#f59e0b';
                                            e.currentTarget.style.boxShadow = '0 0 0 3px rgba(245, 158, 11, 0.1)';
                                        }}
                                        onBlur={(e) => {
                                            e.currentTarget.style.borderColor = '#e2e8f0';
                                            e.currentTarget.style.boxShadow = 'none';
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Destination & Display Order */}
                            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20, marginBottom: 24 }}>
                                <div>
                                    <label style={{ 
                                        display: 'block',
                                        fontSize: 14,
                                        fontWeight: 600,
                                        color: '#1e293b',
                                        marginBottom: 8
                                    }}>
                                        Destination <span style={{ color: '#ef4444' }}>*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.destination}
                                        onChange={(e) => setFormData({ ...formData, destination: e.target.value })}
                                        required
                                        placeholder="e.g., Goa"
                                        style={{
                                            width: '100%',
                                            padding: '12px 16px',
                                            border: '2px solid #e2e8f0',
                                            borderRadius: 10,
                                            fontSize: 15,
                                            fontFamily: 'inherit',
                                            transition: 'all 0.2s ease',
                                            outline: 'none'
                                        }}
                                        onFocus={(e) => {
                                            e.currentTarget.style.borderColor = '#f59e0b';
                                            e.currentTarget.style.boxShadow = '0 0 0 3px rgba(245, 158, 11, 0.1)';
                                        }}
                                        onBlur={(e) => {
                                            e.currentTarget.style.borderColor = '#e2e8f0';
                                            e.currentTarget.style.boxShadow = 'none';
                                        }}
                                    />
                                </div>
                                <div>
                                    <label style={{ 
                                        display: 'block',
                                        fontSize: 14,
                                        fontWeight: 600,
                                        color: '#1e293b',
                                        marginBottom: 8
                                    }}>
                                        Display Order
                                    </label>
                                    <input
                                        type="number"
                                        value={formData.display_order}
                                        onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
                                        placeholder="0"
                                        style={{
                                            width: '100%',
                                            padding: '12px 16px',
                                            border: '2px solid #e2e8f0',
                                            borderRadius: 10,
                                            fontSize: 15,
                                            fontFamily: 'inherit',
                                            transition: 'all 0.2s ease',
                                            outline: 'none'
                                        }}
                                        onFocus={(e) => {
                                            e.currentTarget.style.borderColor = '#f59e0b';
                                            e.currentTarget.style.boxShadow = '0 0 0 3px rgba(245, 158, 11, 0.1)';
                                        }}
                                        onBlur={(e) => {
                                            e.currentTarget.style.borderColor = '#e2e8f0';
                                            e.currentTarget.style.boxShadow = 'none';
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Image URL */}
                            <div style={{ marginBottom: 24 }}>
                                <label style={{ 
                                    display: 'block',
                                    fontSize: 14,
                                    fontWeight: 600,
                                    color: '#1e293b',
                                    marginBottom: 8
                                }}>
                                    Image URL <span style={{ color: '#ef4444' }}>*</span>
                                </label>
                                <input
                                    type="url"
                                    value={formData.image_url}
                                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                                    required
                                    placeholder="https://images.unsplash.com/..."
                                    style={{
                                        width: '100%',
                                        padding: '12px 16px',
                                        border: '2px solid #e2e8f0',
                                        borderRadius: 10,
                                        fontSize: 15,
                                        fontFamily: 'inherit',
                                        transition: 'all 0.2s ease',
                                        outline: 'none'
                                    }}
                                    onFocus={(e) => {
                                        e.currentTarget.style.borderColor = '#f59e0b';
                                        e.currentTarget.style.boxShadow = '0 0 0 3px rgba(245, 158, 11, 0.1)';
                                    }}
                                    onBlur={(e) => {
                                        e.currentTarget.style.borderColor = '#e2e8f0';
                                        e.currentTarget.style.boxShadow = 'none';
                                    }}
                                />
                                {formData.image_url && (
                                    <div style={{ 
                                        marginTop: 12, 
                                        height: 140, 
                                        borderRadius: 10, 
                                        overflow: 'hidden',
                                        backgroundImage: `url(${formData.image_url})`,
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center',
                                        border: '2px solid #e2e8f0'
                                    }}></div>
                                )}
                            </div>

                            {/* Description */}
                            <div style={{ marginBottom: 24 }}>
                                <label style={{ 
                                    display: 'block',
                                    fontSize: 14,
                                    fontWeight: 600,
                                    color: '#1e293b',
                                    marginBottom: 8
                                }}>
                                    Description
                                </label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    rows="3"
                                    placeholder="Brief description of the package..."
                                    style={{
                                        width: '100%',
                                        padding: '12px 16px',
                                        border: '2px solid #e2e8f0',
                                        borderRadius: 10,
                                        fontSize: 15,
                                        fontFamily: 'inherit',
                                        transition: 'all 0.2s ease',
                                        outline: 'none',
                                        resize: 'vertical'
                                    }}
                                    onFocus={(e) => {
                                        e.currentTarget.style.borderColor = '#f59e0b';
                                        e.currentTarget.style.boxShadow = '0 0 0 3px rgba(245, 158, 11, 0.1)';
                                    }}
                                    onBlur={(e) => {
                                        e.currentTarget.style.borderColor = '#e2e8f0';
                                        e.currentTarget.style.boxShadow = 'none';
                                    }}
                                />
                            </div>

                            {/* Highlights */}
                            <div style={{ marginBottom: 24 }}>
                                <label style={{ 
                                    display: 'block',
                                    fontSize: 14,
                                    fontWeight: 600,
                                    color: '#1e293b',
                                    marginBottom: 8
                                }}>
                                    Package Highlights (up to 4)
                                </label>
                                <div style={{ display: 'grid', gap: 10 }}>
                                    {formData.highlights.map((highlight, index) => (
                                        <input
                                            key={index}
                                            type="text"
                                            value={highlight}
                                            onChange={(e) => handleHighlightChange(index, e.target.value)}
                                            placeholder={`Highlight ${index + 1}`}
                                            style={{
                                                width: '100%',
                                                padding: '12px 16px',
                                                border: '2px solid #e2e8f0',
                                                borderRadius: 10,
                                                fontSize: 15,
                                                fontFamily: 'inherit',
                                                transition: 'all 0.2s ease',
                                                outline: 'none'
                                            }}
                                            onFocus={(e) => {
                                                e.currentTarget.style.borderColor = '#f59e0b';
                                                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(245, 158, 11, 0.1)';
                                            }}
                                            onBlur={(e) => {
                                                e.currentTarget.style.borderColor = '#e2e8f0';
                                                e.currentTarget.style.boxShadow = 'none';
                                            }}
                                        />
                                    ))}
                                </div>
                            </div>

                            {/* Active Toggle */}
                            <div style={{ 
                                marginBottom: 28,
                                padding: '16px',
                                background: '#f8fafc',
                                borderRadius: 10,
                                border: '2px solid #e2e8f0'
                            }}>
                                <label style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 12, 
                                    cursor: 'pointer',
                                    userSelect: 'none'
                                }}>
                                    <input
                                        type="checkbox"
                                        checked={formData.active}
                                        onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                                        style={{ 
                                            width: 20, 
                                            height: 20,
                                            cursor: 'pointer',
                                            accentColor: '#f59e0b'
                                        }}
                                    />
                                    <div>
                                        <div style={{ 
                                            fontSize: 15, 
                                            fontWeight: 600, 
                                            color: '#1e293b',
                                            marginBottom: 2
                                        }}>
                                            Active Package
                                        </div>
                                        <div style={{ fontSize: 13, color: '#64748b' }}>
                                            Make this package visible on the website
                                        </div>
                                    </div>
                                </label>
                            </div>

                            {/* Action Buttons */}
                            <div style={{ display: 'flex', gap: 12 }}>
                                <button 
                                    type="submit"
                                    style={{ 
                                        flex: 1,
                                        padding: '14px 24px',
                                        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: 10,
                                        fontSize: 15,
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 8,
                                        transition: 'all 0.2s ease'
                                    }}
                                    onMouseOver={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                        e.currentTarget.style.boxShadow = '0 8px 20px rgba(245, 158, 11, 0.3)';
                                    }}
                                    onMouseOut={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = 'none';
                                    }}
                                >
                                    <i className={`fa-solid ${editingId ? 'fa-save' : 'fa-plus'}`}></i>
                                    {editingId ? 'Update Package' : 'Add Package'}
                                </button>
                                <button 
                                    type="button"
                                    onClick={closeModal}
                                    style={{ 
                                        padding: '14px 24px',
                                        background: 'white',
                                        color: '#64748b',
                                        border: '2px solid #e2e8f0',
                                        borderRadius: 10,
                                        fontSize: 15,
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 8,
                                        transition: 'all 0.2s ease'
                                    }}
                                    onMouseOver={(e) => {
                                        e.currentTarget.style.background = '#f8fafc';
                                        e.currentTarget.style.borderColor = '#cbd5e1';
                                    }}
                                    onMouseOut={(e) => {
                                        e.currentTarget.style.background = 'white';
                                        e.currentTarget.style.borderColor = '#e2e8f0';
                                    }}
                                >
                                    <i className="fa-solid fa-times"></i>
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ShortBreaksTab;
